import { gql, type TypedDocumentNode } from "@apollo/client";
import { PRORATION_FRAGMENT } from "@/pages/billing/orders/[orderId]/queries";
import type { ApplicationDeniedReason } from "../types";
import type { HighestLevelOfEducation } from "../education";
import type { RegistrationOrder } from "./types";
import { APPLICATION_EMPLOYMENT_DETAILS_FRAGMENT, type ApplicationEmploymentDetails } from "./queries";

export type ApplicationStatus = "draft" | "submitted" | "under_review" | "approved" | "denied" | "changes_requested";

export type ApplicationEmploymentType = "CURRENT" | "PREVIOUS";

type UpdateSubmittedApplicationApplicationFields = {
  id: string;
  status?: ApplicationStatus;
  reviewerTaskId?: string | null;
  reviewer?: {
    id: string;
    name: string;
    email: string | null;
  } | null;
  eligibilityStatuses: {
    type: string;
    status: string;
  }[];
  currentEmploymentDetails?: ApplicationEmploymentDetails | null;
  previousEmploymentDetails?: ApplicationEmploymentDetails | null;
};

export type UpdateSubmittedApplicationResponse = {
  updateSubmittedApplication: {
    success: boolean;
    message: string;
    application: UpdateSubmittedApplicationApplicationFields | null;
  };
};

export type ApproveSubmittedApplicationResponse = {
  updateSubmittedApplication: {
    success: boolean;
    message: string;
    application:
      | (UpdateSubmittedApplicationApplicationFields & {
          registrationOrder?: RegistrationOrder | null;
          upcomingDuesOrder?: RegistrationOrder | null;
        })
      | null;
  };
};

export type UpdateSubmittedApplicationVariables = {
  input: {
    id: string;
    reviewerId?: string;
    status?: ApplicationStatus;
    deniedReason?: ApplicationDeniedReason;
    eligibility?: {
      type: string;
      status: string;
    };
    educationDetails?: {
      schoolName: string;
      graduationMonth: string;
      graduationYear: string;
      highestLevelOfEducation: HighestLevelOfEducation;
    };
    employmentClassification?: {
      employmentType: ApplicationEmploymentType;
      jobClassification: string;
      jobTitle: string;
      facilityId?: string | null;
      workingLocation?: ApplicationEmploymentDetails["workingLocation"];
      startDate?: string | null;
    };
    memberEmploymentUpdate?: {
      employmentId: string;
      jobClassification: string;
      jobTitle: string;
      tourCode?: string | null;
      facilityId?: string | null;
    };
    deleteApplicationEmployment?: {
      employmentType: ApplicationEmploymentType;
      facilityId?: string | null;
    };
    internalNote?: string;
    messageToApplicant?: string;
  };
};

export const UPDATE_SUBMITTED_APPLICATION_MUTATION: TypedDocumentNode<
  UpdateSubmittedApplicationResponse,
  UpdateSubmittedApplicationVariables
> = gql`
  mutation UpdateSubmittedApplication($input: BackofficeUpdateSubmittedApplicationInput!) {
    updateSubmittedApplication(input: $input) {
      success
      message
      application {
        id
        status
        reviewerTaskId
        reviewer {
          id
          name
          email
        }
        eligibilityStatuses {
          type
          status
        }
        currentEmploymentDetails {
          ...ApplicationEmploymentDetailsFields
        }
        previousEmploymentDetails {
          ...ApplicationEmploymentDetailsFields
        }
      }
    }
  }
  ${APPLICATION_EMPLOYMENT_DETAILS_FRAGMENT}
`;

export const APPROVE_SUBMITTED_APPLICATION_MUTATION: TypedDocumentNode<
  ApproveSubmittedApplicationResponse,
  UpdateSubmittedApplicationVariables
> = gql`
  mutation ApproveSubmittedApplication($input: BackofficeUpdateSubmittedApplicationInput!) {
    updateSubmittedApplication(input: $input) {
      success
      message
      application {
        id
        status
        registrationOrder {
          id
          status
          paymentStatus
          balance
          lineItemsTotal
          dueDate
          description
          updatedAt
          person {
            displayName
            pgaId
          }
          lineItems {
            id
            name
            priceCents
            proration {
              ...ProrationFragment
            }
          }
        }
        upcomingDuesOrder {
          id
          status
          paymentStatus
          balance
          lineItemsTotal
          dueDate
          description
          updatedAt
          person {
            displayName
            pgaId
          }
          lineItems {
            id
            name
            priceCents
            proration {
              ...ProrationFragment
            }
          }
        }
      }
    }
  }
  ${PRORATION_FRAGMENT}
`;
